import { useState } from "react";
import { useAlerts, type AlertKind } from "./useAlerts";
import { fmtNum } from "../../lib/format";

// Direction A из прототипа: минимум полей — направление + целевая цена.
// Без групп. По умолчанию направление «ниже», цена -5% от текущей.
//
// editRuleId — если задан, в submit'е сначала удаляем старое правило, потом
// создаём новое. Так делаем «изменить цель» с менеджер-страницы без отдельной
// PATCH-логики.
export function AddAlertModal({
  secid,
  secname,
  currentPrice,
  editRuleId,
  initialKind,
  initialThreshold,
  onClose,
}: {
  secid: string;
  secname: string;
  currentPrice: number;
  editRuleId?: number;
  initialKind?: AlertKind;
  initialThreshold?: number;
  onClose: () => void;
}) {
  const { createAlert, isCreating, removeAlertAsync } = useAlerts();
  const [kind, setKind] = useState<AlertKind>(initialKind ?? "price_below");
  const [price, setPrice] = useState<string>(
    initialThreshold != null
      ? initialThreshold.toFixed(2)
      : (currentPrice * 0.95).toFixed(2),
  );

  const target = parseFloat(price);
  const deltaPct =
    Number.isFinite(target) && currentPrice > 0
      ? ((target - currentPrice) / currentPrice) * 100
      : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!Number.isFinite(target) || target <= 0) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    if (editRuleId != null) {
      await removeAlertAsync(editRuleId);
    }
    await createAlert({ secid, kind, threshold: target });
    onClose();
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="modal alert-modal"
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
      >
        <div className="alert-head">
          <div>
            <div className="hand2 label">
              {editRuleId != null ? "изменить алерт" : "новый алерт"}
            </div>
            <div className="alert-bond">{secname}</div>
            <div className="mono soft" style={{ fontSize: 11.5 }}>
              {secid} · сейчас {fmtNum(currentPrice)} ₽
            </div>
          </div>
          <button
            type="button"
            className="btn sm"
            onClick={onClose}
            aria-label="закрыть"
          >
            ×
          </button>
        </div>

        <div className="label" style={{ marginBottom: 6 }}>условие</div>
        <div className="alert-dir-grid">
          <button
            type="button"
            className={`alert-dir${kind === "price_below" ? " on" : ""}`}
            onClick={() => setKind("price_below")}
          >
            <span className="alert-dir-arrow">↓</span>
            <span>цена ниже</span>
          </button>
          <button
            type="button"
            className={`alert-dir${kind === "price_above" ? " on" : ""}`}
            onClick={() => setKind("price_above")}
          >
            <span className="alert-dir-arrow">↑</span>
            <span>цена выше</span>
          </button>
        </div>

        <div className="label" style={{ marginBottom: 6 }}>целевая цена, ₽</div>
        <div className="alert-price-row">
          <input
            type="number"
            step={0.5}
            min={0}
            required
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="alert-price-input"
          />
          <span className="alert-price-hint">
            {deltaPct == null
              ? "—"
              : `${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(1)} % от текущей`}
          </span>
        </div>

        <div className="alert-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="submit"
            className="btn primary"
            disabled={isCreating}
            style={{ flex: 2 }}
          >
            {isCreating
              ? "сохраняю…"
              : editRuleId != null
              ? "Сохранить"
              : "Создать алерт"}
          </button>
        </div>
      </form>
    </div>
  );
}
